import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useBanUserMutation } from 'store/services/admin/adminApi';
import './BanUserModal.css';

interface BanUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  activeTrades: {
    pendingOutboundTrades: number;
    pendingInboundTrades: number;
  };
  onSuccess: () => void;
}

const BanUserModal: React.FC<BanUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  activeTrades,
  onSuccess
}) => {
  const [reason, setReason] = useState<string>('');
  const [confirmText, setConfirmText] = useState<string>('');
  const [banUser, { isLoading }] = useBanUserMutation();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setConfirmText('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isReasonValid = reason.trim().length >= 10 && reason.trim().length <= 1000;
  const isConfirmed = confirmText.toLowerCase() === username.toLowerCase();
  const isValid = isReasonValid && isConfirmed;

  const handleSubmit = async () => {
    if (!isValid) return;

    try {
      const result = await banUser({
        userId,
        body: {
          reason: reason.trim()
        }
      }).unwrap();

      toast.success(`${username} has been banned. ${result.tradesAffected.inboundTrades + result.tradesAffected.outboundTrades} trades require manual resolution.`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to ban user:', error);
      toast.error(error.data?.message || 'Failed to ban user. Please try again.');
    }
  };

  const totalTrades = activeTrades.pendingOutboundTrades + activeTrades.pendingInboundTrades;

  const modalContent = (
    <div className="ban-user-modal-overlay" onClick={onClose}>
      <div className="ban-user-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ban-user-modal-header">
          <h2 className="ban-user-modal-title">Ban User Account</h2>
          <button className="ban-user-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="ban-user-modal-content">
          <div className="danger-banner">
            ⚠️ <strong>Warning:</strong> This action will immediately suspend the user's account.
          </div>

          <div className="user-info-section">
            <div className="info-row">
              <span className="info-label">User to Ban:</span>
              <span className="info-value username-value">{username}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Active Trades:</span>
              <span className="info-value">{totalTrades} trade{totalTrades !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {totalTrades > 0 && (
            <div className="trade-resolution-section">
              <h3 className="section-subtitle">Trade Resolution</h3>
              <div className="trade-counts">
                <div className="trade-count-item">
                  <span className="count-label">Outbound (User is Sender):</span>
                  <span className="count-value">{activeTrades.pendingOutboundTrades}</span>
                </div>
                <div className="trade-count-item">
                  <span className="count-label">Inbound (User is Receiver):</span>
                  <span className="count-value">{activeTrades.pendingInboundTrades}</span>
                </div>
              </div>
              <div className="resolution-info">
                <p>
                  All active trades will require <strong>manual resolution</strong> by an admin.
                  You will need to review each trade individually and decide on appropriate action.
                </p>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="reason" className="form-label">
              Reason for Ban
              <span className="label-required">*</span>
            </label>
            <textarea
              id="reason"
              className="form-textarea"
              placeholder="Provide a detailed explanation for this ban (10-1000 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={5}
              maxLength={1000}
              disabled={isLoading}
            />
            <CharacterCounter
              currentLength={reason.length}
              maxLength={1000}
              warningThreshold={950}
            />
            {reason.length > 0 && reason.length < 10 && (
              <div className="validation-hint error">
                Reason must be at least 10 characters
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirm" className="form-label">
              Type <strong>"{username}"</strong> to confirm
              <span className="label-required">*</span>
            </label>
            <input
              id="confirm"
              type="text"
              className="form-input"
              placeholder={`Type ${username} to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={isLoading}
            />
            {confirmText && !isConfirmed && (
              <div className="validation-hint error">
                Username does not match
              </div>
            )}
          </div>

          <div className="effects-list">
            <h3 className="section-subtitle">What happens when a user is banned:</h3>
            <ul>
              <li>User will be immediately logged out of all active sessions</li>
              <li>User will not be able to log in (error message shown)</li>
              <li>All active trades will require manual admin resolution</li>
              <li>This action will be logged in the admin audit trail</li>
              <li>User can be unbanned later if needed</li>
            </ul>
          </div>
        </div>

        <div className="ban-user-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? 'Banning User...' : 'Ban User'}
            color="default"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isValid || isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BanUserModal;
